import { createContext, useState, useCallback, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE } from '../config';
import { createApi } from './api';

export const AuthContext = createContext(null);

const ROLE_LEVELS = { viewer: 0, editor: 1, admin: 2 };

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const handleLogout = useCallback(() => {
        localStorage.clear();
        setUser(null);
    }, []);

    const api = createApi(handleLogout);

    // On mount, try to restore session from stored token
    useEffect(() => {
        (async () => {
            const token = localStorage.getItem('accessToken');
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    if (decoded.exp * 1000 > Date.now()) {
                        setUser({
                            id: decoded.sub,
                            email: decoded.email,
                            fullname: decoded.fullname || decoded.email,
                            role: decoded.role || 'viewer',
                        });
                        setLoading(false);
                        return;
                    }
                } catch (_) {}
                localStorage.clear();
            }
            setLoading(false);
        })();
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await axios.post(`${API_BASE}/auth/login`, { email, password });
        localStorage.setItem('accessToken', data.access_token);
        if (data.refresh_token) {
            localStorage.setItem('refreshToken', data.refresh_token);
        }
        const decoded = jwtDecode(data.access_token);
        const u = {
            id: decoded.sub,
            email: decoded.email,
            fullname: decoded.fullname || decoded.email,
            role: decoded.role || 'viewer',
        };
        setUser(u);
        return u;
    }, []);

    const logout = useCallback(() => {
        handleLogout();
        // Try to revoke token on server
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
            axios.post(`${API_BASE}/auth/logout`, { refresh_token: refreshToken }).catch(() => {});
        }
        localStorage.clear();
    }, [handleLogout]);

    const refreshProfile = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            if (data) {
                setUser((prev) => ({ ...prev, ...data }));
            }
        } catch (_) {}
    }, [api]);

    const setLang = useCallback(
        async (lang) => {
            try {
                await api.patch('/auth/me', { preferred_lang: lang });
                setUser((prev) => ({ ...prev, preferred_lang: lang }));
                localStorage.setItem('lang', lang);
            } catch (_) {}
        },
        [api]
    );

    const value = {
        user,
        loading,
        login,
        logout,
        refreshProfile,
        setLang,
        lang: user?.preferred_lang || localStorage.getItem('lang') || 'en',
        api,
        hasRole: (role) => ROLE_LEVELS[user?.role] >= ROLE_LEVELS[role],
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}